/**
 * Read an instant's calendar date and clock time in a NAMED timezone.
 *
 * Split out from suggestion-rows.tsx so it is independently testable and
 * because getting this wrong is a data bug, not a display bug: `workDate` on
 * an accepted suggestion is stored, grouped by, submitted, approved and
 * lockdown-checked against - see CLAUDE.md, "workDate is a calendar day...
 * never derived from an instant". Reading it off the BROWSER's local
 * getFullYear/getHours (as an earlier version of this file did) means the
 * same meeting files onto a different calendar day depending on what
 * timezone the viewer's machine happens to be set to, which is the same bug
 * class CLAUDE.md warns about for "today" and for week boundaries.
 *
 * Intl.DateTimeFormat with an explicit `timeZone`, read via formatToParts, is
 * used rather than hand-rolled offset arithmetic - a fixed +05:30 (or any
 * other constant offset) is wrong for DST-observing zones and is wrong the
 * moment the organisation's timezone setting changes, which is exactly the
 * mistake this replaces.
 */

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

export interface ZonedParts {
  /** `YYYY-MM-DD`, the calendar day this instant falls on in `timeZone`. */
  workDate: string;
  /** `HH:MM`, 24-hour, in `timeZone`. */
  clock: string;
}

/**
 * `iso` is an instant (e.g. a calendar event's `startsAt`/`endsAt`). Returns
 * its calendar date and clock time as seen in `timeZone`, regardless of what
 * timezone the current process or browser happens to be running in.
 */
export function zonedParts(iso: string, timeZone: string): ZonedParts {
  const parts = formatterFor(timeZone).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00';

  // en-CA with these options yields 24-hour HH, but formats midnight as "24" in
  // some ICU builds rather than "00" - normalised here so beginTime always
  // matches the API's /^([01]\d|2[0-3]):[0-5]\d$/.
  const hour = get('hour') === '24' ? '00' : get('hour');

  return {
    workDate: `${get('year')}-${get('month')}-${get('day')}`,
    clock: `${hour}:${get('minute')}`,
  };
}
